// Content for /indoor-air-quality (mockup page 6).
//
// The mockup's four product cards are kept, with the marketing sanded off.
// This is the page where an independent site earns its name or doesn't: the
// air-quality aisle is full of devices sold on percentages nobody can trace,
// and two of these four have real caveats a homeowner should hear before
// they spend money, not after.

export interface Solution {
    id: string
    name: string
    /** What it genuinely does. */
    summary: string
    points: string[]
    /** The part the brochure leaves out. Always shown. */
    caveat: string
    icon: string
}

export const SOLUTIONS: Solution[] = [
    {
        id: 'filtration',
        name: 'Advanced Air Filtration',
        summary:
            'A deep media filter — typically 4–5 inches of pleated material at MERV 13 — capturing far more dust, pollen and dander than the thin filter most systems ship with.',
        points: [
            'The longest, dullest track record of anything here',
            'No electronics, nothing to fail',
            'Cheapest per unit of air actually cleaned',
        ],
        caveat:
            'A denser filter restricts airflow. On tight or undersized ductwork, MERV 13 can cost you cooling capacity — ask for a static-pressure reading before committing.',
        icon: '<path d="M2.6 3.4h14.8v13.2H2.6zm2.4 2.4v8.4M8 5.8v8.4M12 5.8v8.4M15 5.8v8.4" stroke="currentColor" stroke-width="1.3" fill="none"/>',
    },
    {
        id: 'uv',
        name: 'UV Light Air Purification',
        summary:
            'A lamp mounted inside the air handler, aimed at the indoor coil and drain pan, running continuously to stop biological growth taking hold on a permanently cold, wet surface.',
        points: [
            'Genuinely effective at keeping a clean coil clean',
            'Helps with the musty smell that arrives when the AC starts',
            'Protects the airflow you paid for',
        ],
        caveat:
            'Coil disinfection is the well-supported use. Claims about killing airborne viruses as they pass through a duct are a different matter — that needs far more dose than the air has contact time for.',
        icon: '<path d="M7.4 2.6h5.2v7.6a2.6 2.6 0 0 1-5.2 0z" stroke="currentColor" stroke-width="1.3" fill="none"/><path d="M10 13.4v4M7.4 6h5.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
    },
    {
        id: 'purifier',
        name: 'Whole-Home Air Purifier',
        summary:
            'An in-duct device aimed at what a filter cannot catch: gases, odours and volatile organic compounds from cooking, pets, cleaning products and new furnishings.',
        points: [
            'The right category for smells rather than particles',
            'Treats the whole house through the existing ductwork',
            'Low maintenance once fitted',
        ],
        caveat:
            'Claims in this category run well ahead of independent testing. Ask which pollutant a unit was tested against and by whom — and avoid anything that works by producing ozone, which is a lung irritant.',
        icon: '<path d="M3.4 4.6h13.2a1 1 0 0 1 1 1v8.8a1 1 0 0 1-1 1H3.4a1 1 0 0 1-1-1V5.6a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.3" fill="none"/><path d="M6 8.2h8M6 11.8h5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
    },
    {
        id: 'electronic',
        name: 'Electronic Air Cleaner',
        summary:
            'Charges particles electrically so they stick to collector plates, which are washed rather than replaced. Capable of catching very fine particles on paper.',
        points: [
            'Washable, reusable cells instead of consumables',
            'Catches finer particles than a basic filter',
        ],
        caveat:
            'Performance falls off sharply as the plates load, so it depends on you washing them. Some designs also generate ozone as a by-product. For most homes a deep media filter does the same job for less money and less fuss — which is why the matcher below never recommends this one.',
        icon: '<path d="M3 4.4h14v11.2H3z" stroke="currentColor" stroke-width="1.3" fill="none"/><path d="M6.2 4.4v11.2M9.4 4.4v11.2M12.6 4.4v11.2M15.8 4.4v11.2" stroke="currentColor" stroke-width="1.1"/>',
    },
]

/**
 * Facts we print with a source attached.
 *
 * Both of these come from the mockup, and both happen to be real EPA
 * findings — so they stay, with the citation the mockup only half gave.
 * Anything on this page that couldn't be sourced was cut instead.
 */
export const EPA_FACTS = [
    {
        stat: '2–5×',
        claim: 'Indoor concentrations of some pollutants run two to five times higher than outdoors.',
        source: 'US EPA, Introduction to Indoor Air Quality',
    },
    {
        stat: '~90%',
        claim: 'Americans spend about 90% of their time indoors.',
        source: 'US EPA, Report on the Environment',
    },
]

export const BENEFITS = [
    {
        title: 'Fewer triggers',
        body: 'Less pollen, dust and dander moving through the house is the part of this with the clearest evidence behind it.',
        icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-.9 7-4.9 7-9.2V4.6z"/>',
    },
    {
        title: 'Less dusting',
        body: 'Filtration that actually catches fine particles means fewer of them settling on every surface a week after you cleaned.',
        icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>',
    },
    {
        title: 'A cleaner coil',
        body: 'Most of what a filter stops would otherwise end up on the indoor coil, where it quietly costs you airflow and efficiency.',
        icon: '<path d="M3 5h14v10H3z" stroke="currentColor" stroke-width="1.4" fill="none"/><path d="M6 5v10M10 5v10M14 5v10" stroke="currentColor" stroke-width="1.2"/>',
    },
    {
        title: 'Nothing oversold',
        body: "We'll tell you when the honest answer is a better filter and a calendar reminder rather than a device with a plug on it.",
        icon: '<path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm-.9 11.7L5.8 10.4l1.3-1.3 2 2 4.1-4.1 1.3 1.3z"/>',
    },
]
